import queryString from "query-string"
import { balanceEndpoints } from "store/apis/balanceAPI/balance.api"
import { IPaginationReq } from "utils/interfaces"
import { TAGS, api } from "../../api"
import {
  IActiveElixir,
  IActivePremiumElixir,
  IBuyElixirResponse,
  IElixir,
  IElixirsFilters,
  IElixirsResponse
} from "./types"

export const elixirsAPI = api.enhanceEndpoints({
  addTagTypes: [
    TAGS.ELIXIRS,
    TAGS.ELIXIRS_COUNT,
    TAGS.ELIXIRS_INVENTORY,
    TAGS.ELIXIRS_ACTIVATED
  ]
})

export const elixirsEndpoints = elixirsAPI.injectEndpoints({
  endpoints: (builder) => ({
    getActivatedPremiumElixirs: builder.query<IActivePremiumElixir[], void>({
      query: () => ({
        url: "/elixirs/activated-premium-elixirs",
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: TAGS.ELIXIRS_ACTIVATED as const,
                id: _id
              })),
              { type: TAGS.ELIXIRS_ACTIVATED, id: "LIST" }
            ]
          : [{ type: TAGS.ELIXIRS_ACTIVATED, id: "LIST" }]
    }),
    getActivatedElixirs: builder.query<IActiveElixir[], void>({
      query: () => ({
        url: "/elixirs/activated-elixirs",
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: TAGS.ELIXIRS_ACTIVATED as const,
                id: _id
              })),
              { type: TAGS.ELIXIRS_ACTIVATED, id: "LIST" }
            ]
          : [{ type: TAGS.ELIXIRS_ACTIVATED, id: "LIST" }]
    }),
    getCount: builder.query<number, string>({
      query: (templateId: string) => ({
        url: `/elixirs/elixirs-instances/count/${templateId}`,
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: (_, _err, id) => [
        { type: TAGS.ELIXIRS_COUNT, id: `count_${id}` }
      ]
    }),
    getAllElixirs: builder.query<IElixir[], void>({
      query: () => ({
        url: "/elixirs",
        method: "GET"
      }),
      extraOptions: { maxRetries: 2 },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: TAGS.ELIXIRS as const,
                id: _id
              })),
              { type: TAGS.ELIXIRS, id: "LIST" }
            ]
          : [{ type: TAGS.ELIXIRS, id: "LIST" }]
    }),
    getUsersElixirs: builder.query<
      IElixirsResponse,
      IPaginationReq & IElixirsFilters
    >({
      query: ({ page, limit, elixirModels, ...filters }) => {
        const qs = queryString.stringify(
          {
            page,
            limit,
            ...filters,
            elixirModels:
              elixirModels && elixirModels.length
                ? elixirModels.join(",")
                : undefined
          },
          { skipNull: false, skipEmptyString: true }
        )
        return { url: `/elixirs/elixirs-instances?${qs}`, method: "GET" }
      },
      extraOptions: { maxRetries: 2 },
      providesTags: (result) =>
        result
          ? [
              ...(result?.items || []).map(({ _id }) => ({
                type: TAGS.ELIXIRS_INVENTORY as const,
                id: _id
              })),
              { type: TAGS.ELIXIRS_INVENTORY, id: "LIST" }
            ]
          : [{ type: TAGS.ELIXIRS_INVENTORY, id: "LIST" }]
    }),
    buyElixir: builder.mutation<IBuyElixirResponse, string>({
      query: (id) => ({
        url: `/elixirs/buy/${id}`,
        method: "PATCH"
      }),
      extraOptions: { maxRetries: 2 },
      invalidatesTags: [
        { type: TAGS.ELIXIRS, id: "LIST" },
        // ELIXIRS_COUNT removed — updated via updateQueryData below
        { type: TAGS.ELIXIRS_INVENTORY }
      ],
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          elixirsEndpoints.util.updateQueryData(
            "getCount",
            id,
            (draft) => (draft ?? 0) + 1
          )
        )

        try {
          const { data } = await queryFulfilled

          dispatch(
            elixirsEndpoints.util.updateQueryData(
              "getCount",
              data.elixirInstance.elixir._id,
              () => data.elixirInstance.count
            )
          )

          dispatch(
            balanceEndpoints.util.updateQueryData(
              "getBalance",
              undefined,
              () => data.newBalance
            )
          )
        } catch {
          patchResult.undo()
        }
      }
    }),
    activateElixir: builder.mutation<
      { instanceId: string; activeElixirs: IActiveElixir[] },
      string
    >({
      query: (id) => ({
        url: `/elixirs/activate/${id}`,
        method: "PATCH"
      }),
      extraOptions: { maxRetries: 2 },
      invalidatesTags: [
        // ELIXIRS_COUNT removed — updated via updateQueryData below
        { type: TAGS.ELIXIRS_INVENTORY },
        { type: TAGS.ELIXIRS_ACTIVATED }
      ],
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          elixirsEndpoints.util.updateQueryData("getCount", id, (draft) =>
            Math.max((draft ?? 0) - 1, 0)
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      }
    }),
    activatePremiumElixir: builder.mutation<
      { instanceId: string; activeElixirs: IActiveElixir[] },
      string
    >({
      query: (id) => ({
        url: `/elixirs/activate/premium/${id}`,
        method: "PATCH"
      }),
      extraOptions: { maxRetries: 2 },
      invalidatesTags: [
        // ELIXIRS_COUNT removed — updated via updateQueryData below
        { type: TAGS.ELIXIRS_INVENTORY },
        { type: TAGS.ELIXIRS_ACTIVATED }
      ],
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          elixirsEndpoints.util.updateQueryData("getCount", id, (draft) =>
            Math.max((draft ?? 0) - 1, 0)
          )
        )
        try {
          await queryFulfilled
        } catch {
          patchResult.undo()
        }
      }
    })
  })
})
